import React, { useState, useEffect } from 'react';
import { LinkIcon, CheckCircleIcon } from '@heroicons/react/24/solid';
import { createCode } from '../../util/db';
import { useAuth } from '../../util/auth';

const processNames = { sb: "Stairs & Buckets", proc3: "Finding the Money", proc6: "Liability Wall" };
// Only S&B has multiple translated videos; proc3/proc6 are English-only
const hasLanguagePicker = { sb: true };

const ShareLinkModal = ({ isOpen, onClose, process = "sb" }) => {
  const auth = useAuth();
  const [step, setStep] = useState('language'); // 'language' or 'link'
  const [language, setLanguage] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // For processes without a language picker, auto-generate the link on open
  useEffect(() => {
    if (isOpen && !hasLanguagePicker[process] && step === 'language' && !isGenerating) {
      handleLanguageSelect('English');
    }
  }, [isOpen, process]);

  function generateRandomHash() {
    const array = new Uint8Array(16);
    window.crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  const handleLanguageSelect = async (selectedLanguage) => {
    setLanguage(selectedLanguage);
    setIsGenerating(true);

    try {
      const hash = generateRandomHash();
      const link = `https://insuranceagencytraining.org/view/${hash}`;

      // Create code in database
      await createCode(hash, {
        owner: auth.user.uid,
        language: selectedLanguage,
        process: process,
        opened: false,
        views: 0,
        watchSeconds: 0,
        method: 'magic_link',
        recipients: ['Shared Link'],
      });

      setGeneratedLink(link);
      setStep('link');
    } catch (error) {
      console.error('Error creating share link:', error);
      alert('Failed to generate link. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      alert('Failed to copy link. Please copy manually.');
    }
  };

  const handleCreateAnother = () => {
    setStep('language');
    setLanguage('');
    setGeneratedLink('');
    setCopied(false);
  };

  const handleClose = () => {
    // Reset state when closing
    setStep('language');
    setLanguage('');
    setGeneratedLink('');
    setCopied(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="z-20 sm:fixed absolute inset-0 bg-black bg-opacity-75 flex justify-center items-center">
      <div className="bg-white p-8 rounded-lg shadow-lg lg:w-1/2 w-5/6 max-w-4xl relative">
        <button
          onClick={handleClose}
          className="absolute top-0 right-4 text-3xl text-gray-400 rounded-md"
        >
          &times;
        </button>

        {step === 'language' && hasLanguagePicker[process] && (
          <>
            <h2 className="text-center text-3xl font-bold pb-2">Share {processNames[process] || "Video"} with Client</h2>
            <p className="text-gray-600 text-center mb-6">
              Select the language for the video your client will watch
            </p>

            <p className="text-gray-400 text-left m-2">Select a Language</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {['English New V2', 'English', 'Spanish', 'Mandarin', 'Cantonese'].map((lang) => (
                <button
                  key={lang}
                  onClick={() => handleLanguageSelect(lang)}
                  disabled={isGenerating}
                  type="button"
                  className="relative inline-flex items-center rounded-full bg-white text-gray-400 ring-gray-300 px-3 py-2 text-sm ring-1 ring-inset hover:bg-gray-50 focus:z-10 disabled:opacity-50"
                >
                  {lang}
                </button>
              ))}
            </div>

            {isGenerating && (
              <p className="text-center text-gray-500">Generating link...</p>
            )}
          </>
        )}

        {step === 'language' && !hasLanguagePicker[process] && (
          <div className="text-center">
            <h2 className="text-3xl font-bold pb-2">Share {processNames[process] || "Video"} with Client</h2>
            <p className="text-gray-500 mt-4">Generating link...</p>
          </div>
        )}

        {step === 'link' && (
          <>
            <div className="flex flex-col text-center space-y-4">
              <LinkIcon className="h-12 text-green-700 mx-auto" />
              <h2 className="text-4xl font-semibold">Your Share Link is Ready!</h2>
              <p className="text-lg font-semithin">
                Copy this link and share it with your client through your preferred method (email, text, etc.)
              </p>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-600 mb-2">
                  {processNames[process] || "Video"}
                  {hasLanguagePicker[process] && <> &middot; <span className="font-semibold">{language}</span></>}
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={generatedLink}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white"
                  />
                  <button
                    onClick={handleCopy}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md font-semibold ${copied
                        ? 'bg-green-600 text-white'
                        : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                  >
                    {copied ? (
                      <>
                        <CheckCircleIcon className="h-5 w-5" />
                        Copied!
                      </>
                    ) : (
                      'Copy Link'
                    )}
                  </button>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-left">
                <h3 className="font-semibold text-green-900 mb-2">Instructions:</h3>
                <ul className="text-sm text-green-800 space-y-1 list-disc list-inside">
                  <li>Click "Copy Link" to copy the URL to your clipboard</li>
                  <li>Paste the link in your email, text message, or messaging app</li>
                  <li>Send it to your client</li>
                  <li>The link is valid for 24 hours from the time they click on it</li>
                </ul>
              </div>

              <div className="pt-4">
                <button
                  onClick={handleCreateAnother}
                  className="text-green-600 hover:text-green-700 underline font-semibold"
                >
                  Create another link
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ShareLinkModal;
